import { useState } from "react";
import { Settings, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RelayItem } from "@/components/RelayItem";
import { NewRelayForm } from "@/components/NewRelayForm";
import { DEFAULT_GATEWAY } from "@/hooks/useNsiteGateway";
import { nsiteRelays } from "@/services/settings";
import { use$ } from "@/hooks/use$";

interface GatewaySettingsDialogProps {
  gateway: string;
  onGatewayChange: (gateway: string) => void;
}

const PRESET_GATEWAYS = [
  { label: "nsite.lol", value: "nsite.lol" },
  { label: "nsite.run", value: "nsite.run" },
  { label: "nosto.re", value: "nosto.re" },
];

export function GatewaySettingsDialog({
  gateway,
  onGatewayChange,
}: GatewaySettingsDialogProps) {
  const [open, setOpen] = useState(false);
  const [localGateway, setLocalGateway] = useState(gateway);

  const relays = use$(nsiteRelays) ?? [];

  const handleSaveGateway = () => {
    const trimmed = localGateway.trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
    onGatewayChange(trimmed || DEFAULT_GATEWAY);
    setOpen(false);
  };

  const handleResetGateway = () => {
    setLocalGateway(DEFAULT_GATEWAY);
    onGatewayChange(DEFAULT_GATEWAY);
  };

  const handleAddRelay = (relay: string) => {
    nsiteRelays.next([...new Set([...relays, relay])]);
  };

  const handleRemoveRelay = (relay: string) => {
    nsiteRelays.next(relays.filter((r) => r !== relay));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setLocalGateway(gateway); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9 border-border/60 text-muted-foreground hover:text-foreground"
        >
          <Settings className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Settings</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Feed Settings</DialogTitle>
          <DialogDescription>
            Configure the gateway and relays used for the nsite feed.
          </DialogDescription>
        </DialogHeader>

        {/* Gateway section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Gateway</h3>
          <p className="text-xs text-muted-foreground">
            The gateway used to open and preview nsite URLs.
          </p>
          <div className="space-y-2">
            <Label htmlFor="gateway-input" className="text-xs">Gateway domain</Label>
            <Input
              id="gateway-input"
              value={localGateway}
              onChange={(e) => setLocalGateway(e.target.value)}
              placeholder="nsite.lol"
              className="font-mono text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESET_GATEWAYS.map((preset) => (
              <Button
                key={preset.value}
                variant={localGateway === preset.value ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs font-mono"
                onClick={() => setLocalGateway(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs text-muted-foreground hover:text-foreground h-7 px-2"
              onClick={handleResetGateway}
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </Button>
            <Button size="sm" className="h-7" onClick={handleSaveGateway}>
              Save gateway
            </Button>
          </div>
        </div>

        <Separator />

        {/* Relays section */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Feed Relays</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Relays queried for nsite events (kinds 15128 and 35128).
            </p>
          </div>
          <div className="space-y-2">
            {relays.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No relays configured.</p>
            ) : (
              relays.map((relay) => (
                <RelayItem
                  key={relay}
                  relay={relay}
                  onRemove={() => handleRemoveRelay(relay)}
                />
              ))
            )}
          </div>
          <NewRelayForm onAdd={handleAddRelay} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
